import {
  SimpleGrid,
  Notification,
  Stack,
  rem,
  Button,
  Modal,
  Select,
} from '@mantine/core';
import { ClientStatusCard } from './Cards/ClientStatusCard';
import { WorkersCard } from './Cards/WorkersCard';
import { OtherInfoCard } from './Cards/OtherInfoCard';
import { RequirementsSettingsCard } from './Cards/RequirementsSettingsCard';
import { CustomPriceCard } from './Cards/CustomPriceCard';

import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteFetcher,
  getFetcher, postFetcher,
  putFetcher,
} from '@/api/fetchers';
import useSWR, { useSWRConfig } from 'swr';
import { createFormContext, zodResolver } from '@mantine/form';
import React, {useContext, useEffect, useState} from 'react';
import { IconX } from '@tabler/icons-react';
import { downloadFileWithBearerToken, isEmptyForm} from '@/utils/functions';
import { carePlanToUpdateCarePlan } from '@/schemas/converters/clientSchemasConverter';
import {
  type CarePlanDetailed,
  type CarePlan,
  carePlanSchema,
} from '@/schemas/API/GET/client/carePlan/response';
import { ServiceContext } from '@/layouts/Person/PersonLayout';
import { modals } from '@mantine/modals';
import { type ClientDetailed } from '@/schemas/API/GET/client/response';
import {
  type Service,
  type Services,
} from '@/schemas/API/GET/service/response';
import { type Requirement } from '@/schemas/API/GET/client/carePlan/requirement/response';
import { type ClientAttachments } from '@/schemas/API/GET/client/attachment/response';
import { careRequirementsSchema } from '@/schemas/API/common/basic/careRequirement';
import { ButtonBar } from '@/components/Form/ButtonBar/ButtonBar';
import {
  displayError,
  displayErrorOnMantineForm,
} from '@/utils/error/errorHandler';
import {
  goodNotification,
  okNotification,
} from '@/components/Notification/Notification';
import {useDisclosure} from "@mantine/hooks";
import {AuditModal} from "@/pages/Clients/Client/IPP/Modals/AuditModal";
import { useExportStatus } from '@/hooks/export/useExportStatus';
import { PlanNotFound } from '@/pages/Clients/Client/IPP/Cards/PlanNotFound';

export const [
  ClientIPPFormProvider,
  useClientIPPFormContext,
  useClientIPPForm,
] = createFormContext<CarePlanDetailed>();

export const [
  RequirementFormProvider,
  useRequirementFormContext,
  useRequirementForm,
] = createFormContext<Requirement>();

export const ClientIppSection: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{
    clientId: string;
  }>();
  const { service } = useContext(ServiceContext);

  const { data: client } = useSWR<ClientDetailed>(
    `/clients/${clientId}`,
    getFetcher
  );

  const { data: attachments } = useSWR<ClientAttachments>(
    `/clients/${clientId}/attachments`,
    getFetcher
  );

  const [openedAuditsModal, { open: openAuditsModal, close: closeAuditsModal }] = useDisclosure(false);

  const { mutate } = useSWRConfig();

  const { data: careplan, mutate: mutateCareplan } = useSWR<CarePlan>(
    client && service ? [`/clients/${clientId}/careplans`, service] : null,
    async ([url, service]) => {
      const _service = service as Service;
      const _client = client as ClientDetailed;

      const carePlanShort = _client.carePlans.find(
        cp => cp.serviceId === _service.id
      );

      if (!carePlanShort) return undefined;

      return await getFetcher(`${url}/${carePlanShort.carePlanId}`);
    }
  );

  const careplanform = useClientIPPForm({
    validate: zodResolver(carePlanSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const requirementForm = useRequirementForm({
    validate: zodResolver(careRequirementsSchema),
    validateInputOnChange: true,
    validateInputOnBlur: true,
  });

  const updateClient = async (): Promise<void> => {
    try {
      const carePlanUpdate = carePlanToUpdateCarePlan(careplanform.values);
      await putFetcher(
        `clients/${clientId}/careplans/${careplanform.values.id}`,
        carePlanUpdate
      );
      careplanform.clearErrors();
      mutateCareplan();
      goodNotification('Změny byly uloženy');
    } catch (err) {
      displayErrorOnMantineForm(err, careplanform.setFieldError);
    }
  };

  useEffect(() => {
    if (!careplan || !service) return;

    careplanform.setValues(careplan);
  }, [careplan, service]);

  function resetForm(): void {
    if (!careplan) return;
    careplanform.setValues(careplan);
    careplanform.clearErrors();
    okNotification('Storno...', 'Změny byly zahozeny úspěšně!');
  }

  const [isExporting, setIsExporting] = useState(false);
  const { checkExportStatus } = useExportStatus();

  const handleDownloadCarePlan = async () => {
    try {
      setIsExporting(true);
      const requestData = {
          id: careplanform.values.id,
      };
      const { exportId } = await postFetcher('/exports/careplans', requestData);
      const exportStatus = await checkExportStatus(exportId);
      if (exportStatus?.state === 'FINISHED' && exportStatus?.url) {
        await downloadFileWithBearerToken(exportStatus.url);
      } else {
        console.error('Export failed or no download URL available.');
      }
    } catch (error) {
      console.error('Error exporting careplan:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const deactivateCareplan = async (): Promise<void> => {
    try {
      await deleteFetcher(
        `/clients/${clientId}/careplans/${careplanform.values.id}`
      );
      await mutate(`clients/${clientId}/careplans/${careplanform.values.id}`);
      goodNotification('Plán péče byl smazán.');
      navigate(`/clients/${clientId}/personal`);
    } catch (err) {
      displayError(err);
    }
  };

  function showDeactivationModal(): void {
    modals.openConfirmModal({
      title: 'Smazat plán péče',
      centered: true,
      children: <div>Určitě chcete plán péče smazat? </div>,
      labels: { confirm: 'Smazat', cancel: 'Zrušit' },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => {
        void deactivateCareplan();
      },
    });
  }

  const errIcon = <IconX style={{ width: rem(20), height: rem(20) }} />;

  if (!isEmptyForm(careplanform) && careplan) {
    return (
      <ClientIPPFormProvider form={careplanform}>
        <RequirementFormProvider form={requirementForm}>
          <SimpleGrid>
            <RequirementsSettingsCard />
          </SimpleGrid>
          <SimpleGrid>
            <ClientStatusCard />
          </SimpleGrid>
          <SimpleGrid cols={2}>
            <OtherInfoCard />
            <Stack >
              <WorkersCard />
              <CustomPriceCard clientId={clientId} service={service}/>
            </Stack>
          </SimpleGrid>

          <Modal
              opened={openedAuditsModal}
              onClose={closeAuditsModal}
              centered
              withCloseButton={false}
              size="lg"
          >
            <AuditModal></AuditModal>
          </Modal>

          <ButtonBar>
            <Button variant="outline"
                    radius="md"
                    size="md"
                    className="text-lg"
                    onClick={openAuditsModal}>
              Zobrazit historii úprav
            </Button>
            <Button
              variant="outline"
              radius="md"
              size="md"
              className="text-lg"
              onClick={resetForm}
            >
              Storno
            </Button>
            <Button
              variant="outline"
              radius="md"
              size="md"
              className="text-lg"
              onClick={handleDownloadCarePlan}
              disabled={isExporting}
            >
              {isExporting ? 'Stahuje se...' : 'Stáhnout PDF'}
            </Button>
            <Button
              radius="md"
              size="md"
              className="text-lg"
              color="red"
              onClick={showDeactivationModal}
            >
              Smazat
            </Button>
            <Button
              radius="md"
              size="md"
              className="text-lg"
              onClick={updateClient}
            >
              Uložit
            </Button>
          </ButtonBar>
        </RequirementFormProvider>
      </ClientIPPFormProvider>
    );
  }

  if (!careplan && clientId) {
    return <PlanNotFound clientId={clientId} />;
  }

  return (
    <Notification
      radius="md"
      withBorder
      withCloseButton={false}
      icon={errIcon}
      color="red"
      title="Oups!"
      styles={{
        root: { boxShadow: 'none' },
      }}
    >
      Something went wrong
    </Notification>
  );
};
